import { REACT_QUERY_KEYS } from '@/constants'

// api バレル (`./api`) 経由で読むと api → utils → query-keys の循環になるため、
// クエリ型は定義ファイルを直接参照する
import { type FetchDispatchDetailsHistoryQuery } from './api/fetch-dispatch-details-history'
import { type FetchDispatchesQuery } from './api/fetch-dispatches'
import { type FetchGroupSummaryQuery } from './api/fetch-group-summary'
import { type FetchNotDispatchedShipmentOrderDispatchGroupsQuery } from './api/fetch-not-dispatched-shipment-order-dispatch-groups'
import { type FetchNotDispatchedShipmentOrdersQuery } from './api/fetch-not-dispatched-shipment-orders'
import { type FetchShipmentOrdersTotalAmountsQuery } from './api/fetch-shipment-orders-total-amounts'
import { type FetchVehicleDispatchBoardsQuery } from './api/fetch-vehicle-dispatch-boards'

export const vehicleDispatchBoardsQueryKeys = {
  all: [REACT_QUERY_KEYS.VEHICLE_DISPATCH_BOARDS] as const,
  list: (query: FetchDispatchesQuery) =>
    [...vehicleDispatchBoardsQueryKeys.all, query] as const,
  notDispatchedShipmentOrdersList: (
    query: FetchNotDispatchedShipmentOrdersQuery
  ) =>
    [
      ...vehicleDispatchBoardsQueryKeys.all,
      'notDispatchedShipmentOrdersList',
      query
    ] as const,
  allNotDispatchedShipmentOrdersList: (
    query: FetchNotDispatchedShipmentOrdersQuery
  ) =>
    [
      ...vehicleDispatchBoardsQueryKeys.all,
      'allNotDispatchedShipmentOrdersList',
      query
    ] as const,
  notDispatchedShipmentOrderDispatchGroups: (
    query: FetchNotDispatchedShipmentOrderDispatchGroupsQuery
  ) =>
    [
      ...vehicleDispatchBoardsQueryKeys.all,
      'notDispatchedShipmentOrderDispatchGroups',
      query
    ] as const,
  shipmentOrderDetail: (query: { tenantKey: string; orderId: number }) =>
    [
      ...vehicleDispatchBoardsQueryKeys.all,
      'shipmentOrderDetail',
      query
    ] as const,
  dispatchDetail: (query: {
    tenantKey: string
    dispatchId: number
    id: number
  }) => [...vehicleDispatchBoardsQueryKeys.all, 'dispatchDetail', query],
  notDispatchedShipmentOrdersTotalWeight: (
    query: FetchNotDispatchedShipmentOrdersQuery
  ) =>
    [
      ...vehicleDispatchBoardsQueryKeys.all,
      'notDispatchedShipmentOrdersTotalWeight',
      query
    ] as const,
  dispatchDetailsHistory: (query: FetchDispatchDetailsHistoryQuery) =>
    [
      ...vehicleDispatchBoardsQueryKeys.all,
      'dispatchDetailsHistory',
      query
    ] as const,

  shipmentOrdersTotalAmounts: (query: FetchShipmentOrdersTotalAmountsQuery) =>
    [
      ...vehicleDispatchBoardsQueryKeys.all,
      'shipmentOrdersTotalAmounts',
      query
    ] as const,

  groupSummary: (query: FetchGroupSummaryQuery) =>
    [...vehicleDispatchBoardsQueryKeys.all, 'groupSummary', query] as const,

  /** 板（新API vehicle-dispatch-boards）キャッシュのキー prefix。プレフィックスマッチの invalidate・走査に使う */
  vehicleDispatchBoards: () =>
    [...vehicleDispatchBoardsQueryKeys.all, 'vehicleDispatchBoards'] as const,

  /** 板（新API vehicle-dispatch-boards）の取得クエリキー */
  vehicleDispatchBoardsList: (query: FetchVehicleDispatchBoardsQuery) =>
    [...vehicleDispatchBoardsQueryKeys.vehicleDispatchBoards(), query] as const
}

/**
 * 未配車系（一覧・全件・合計重量・束ねカード）クエリの type 名。
 * invalidate 対象の列挙・楽観更新のキー組み立てで共有する。
 * satisfies でプロパティ名との一致を型レベルで保証する。
 */
export const notDispatchedShipmentOrderQueryTypes = [
  'notDispatchedShipmentOrdersList',
  'allNotDispatchedShipmentOrdersList',
  'notDispatchedShipmentOrdersTotalWeight',
  'notDispatchedShipmentOrderDispatchGroups'
] as const satisfies readonly (keyof typeof vehicleDispatchBoardsQueryKeys)[]

export type NotDispatchedShipmentOrderQueryType =
  (typeof notDispatchedShipmentOrderQueryTypes)[number]
